import { useEffect, useMemo, useReducer, useRef } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { runTapEffect } from '../effects/tapEffect';
import { CelebrationEffect } from '../effects/CelebrationEffect';
import { ButtonColorsSection } from '../features/themes/editor/ButtonColorsSection';
import { CelebrationSection } from '../features/themes/editor/CelebrationSection';
import { ColorsSection } from '../features/themes/editor/ColorsSection';
import { DecorationSection } from '../features/themes/editor/DecorationSection';
import { NameField } from '../features/themes/editor/NameField';
import { SoundSection } from '../features/themes/editor/SoundSection';
import { TapEffectSection } from '../features/themes/editor/TapEffectSection';
import { useSyncedStore, addCustomTheme, updateCustomTheme } from '../store/synced';
import { applyTheme } from '../themes/applyTheme';
import { buildCustomTheme } from '../themes/customTheme';
import { buildBlankCustomTheme, buildCustomThemeFromPreset } from '../themes/customThemeDefaults';
import { getTheme } from '../themes/registry';
import type { CustomTheme } from '../themes/types';
import { uid } from '../lib/uid';
import { useActiveTheme } from '../themes/useActiveTheme';

// Theme editor — creates or edits a CustomTheme in the family library.
// The draft is held in useReducer state; every change re-applies the
// synthesized theme to :root for live preview. Save commits via the
// store mutations; Peruuta restores the previously-active theme.
//
// Sections are separate components (see src/features/themes/editor/).
// Each takes { value, onChange } — the reducer patches the draft on
// every dispatch.

type DraftAction =
  | { type: 'patch'; patch: Partial<CustomTheme> }
  | { type: 'replace'; draft: CustomTheme };

function draftReducer(state: CustomTheme, action: DraftAction): CustomTheme {
  switch (action.type) {
    case 'patch':
      return { ...state, ...action.patch };
    case 'replace':
      return action.draft;
  }
}

function initialDraft(
  fromPresetKey: string | null,
  fromCustom: CustomTheme | undefined,
  editing: CustomTheme | undefined,
): CustomTheme {
  const now = new Date().toISOString();
  if (editing) return editing;
  if (fromCustom) {
    return {
      ...fromCustom,
      id: uid(),
      name: `${fromCustom.name} (kopio)`,
      origin: { kind: 'duplicated', from: fromCustom.id },
      createdAt: now,
      updatedAt: now,
    };
  }
  if (fromPresetKey) {
    const preset = getTheme(fromPresetKey);
    return buildCustomThemeFromPreset(preset, uid(), preset.name, now);
  }
  return buildBlankCustomTheme(uid(), 'Uusi teema', now);
}

export function ThemeEditorPage() {
  const params = useParams<{ themeId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tree = useSyncedStore((s) => s.tree);
  const setTree = useSyncedStore((s) => s.setTree);
  const isCreate = !params.themeId || params.themeId === 'new';
  const editing = useMemo(
    () => (isCreate ? undefined : tree?.customThemes.find((c) => c.id === params.themeId)),
    [isCreate, tree, params.themeId],
  );
  const fromPresetKey = searchParams.get('fromPreset');
  const fromCustomId = searchParams.get('fromCustom');
  const fromCustom = useMemo(
    () => (fromCustomId ? tree?.customThemes.find((c) => c.id === fromCustomId) : undefined),
    [fromCustomId, tree],
  );

  const [draft, dispatch] = useReducer(draftReducer, undefined, () =>
    initialDraft(fromPresetKey, fromCustom, editing),
  );

  const activeTheme = useActiveTheme();
  const previewTheme = useMemo(() => buildCustomTheme(draft), [draft]);

  // Live-apply the draft on every change; on unmount / cancel, revert.
  const restoreRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    restoreRef.current?.();
    restoreRef.current = applyTheme(previewTheme);
    return () => {
      restoreRef.current?.();
      restoreRef.current = null;
    };
  }, [previewTheme]);

  const onCancel = () => {
    restoreRef.current?.();
    restoreRef.current = null;
    applyTheme(activeTheme);
    navigate('/settings');
  };

  const onSave = () => {
    if (!tree) return;
    const now = new Date().toISOString();
    if (isCreate) {
      setTree(addCustomTheme(tree, { ...draft, updatedAt: now }, now));
    } else {
      setTree(updateCustomTheme(tree, draft.id, { ...draft, updatedAt: now }, now));
    }
    navigate('/settings');
  };

  if (!tree) return null;
  if (!isCreate && !editing) {
    return (
      <div className="theme-editor theme-editor--missing">
        <p>Teemaa ei löydy.</p>
        <Link to="/settings">Takaisin asetuksiin</Link>
      </div>
    );
  }

  return (
    <div className="theme-editor">
      <header className="theme-editor__toolbar">
        <button type="button" onClick={onCancel}>
          Peruuta
        </button>
        <h1>{isCreate ? 'Uusi teema' : 'Muokkaa teemaa'}</h1>
        <button type="button" onClick={onSave} className="primary">
          Tallenna
        </button>
      </header>

      <ThemeEditorPreview draft={draft} previewTheme={previewTheme} />

      <NameField
        value={draft.name}
        onChange={(name) => dispatch({ type: 'patch', patch: { name } })}
      />
      <ColorsSection
        value={draft.recipe}
        onChange={(recipe) => dispatch({ type: 'patch', patch: { recipe } })}
      />
      <ButtonColorsSection value={draft} onChange={(patch) => dispatch({ type: 'patch', patch })} />
      <DecorationSection
        value={draft.decoration}
        onChange={(decoration) => dispatch({ type: 'patch', patch: { decoration } })}
      />
      <TapEffectSection
        value={draft.tapEffect}
        onChange={(tapEffect) => dispatch({ type: 'patch', patch: { tapEffect } })}
      />
      <CelebrationSection
        value={draft.celebration}
        onChange={(celebration) => dispatch({ type: 'patch', patch: { celebration } })}
      />
      <SoundSection
        value={draft.sound}
        onChange={(sound) => dispatch({ type: 'patch', patch: { sound } })}
      />
    </div>
  );
}

interface PreviewProps {
  readonly draft: CustomTheme;
  readonly previewTheme: ReturnType<typeof buildCustomTheme>;
}

function ThemeEditorPreview({ draft, previewTheme }: PreviewProps) {
  const [showCelebration, toggleCelebration] = useReducer((s) => !s, false);
  const cardRef = useRef<HTMLButtonElement | null>(null);
  const counterRef = useRef<HTMLDivElement | null>(null);

  const onTapPreview = () => {
    if (!cardRef.current) return;
    runTapEffect(draft.tapEffect, {
      from: cardRef.current,
      to: counterRef.current,
      rewardEmoji: '⭐',
    });
  };

  return (
    <section className="theme-editor__preview" aria-label="Esikatselu">
      <div className="theme-editor__preview-surface">
        <button
          type="button"
          ref={cardRef}
          className="theme-editor__preview-card"
          style={{ background: previewTheme.routineCardPalette[0] }}
          onClick={onTapPreview}
        >
          Kokeile tehtävän napautusta
        </button>
        <div ref={counterRef} className="theme-editor__preview-counter" aria-hidden="true">
          ⭐
        </div>
      </div>
      <button type="button" onClick={toggleCelebration}>
        {showCelebration ? 'Piilota juhla' : 'Näytä juhla'}
      </button>
      {showCelebration ? (
        <CelebrationEffect spec={draft.celebration} palette={previewTheme.routineCardPalette} />
      ) : null}
    </section>
  );
}
